import React from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { node, object, oneOfType, func, bool } from 'prop-types'
import { components } from 'react-select'
import { Paper } from '@components'

function NoOptionsMessage({ selectProps, innerProps, children }) {
  return (
    <Typography
      color="text.secondary"
      className={selectProps.classes.noOptionsMessage}
      {...innerProps}
    >
      {children}
    </Typography>
  )
}

NoOptionsMessage.propTypes = {
  children: node.isRequired,
  innerProps: object,
  selectProps: object.isRequired,
}

const inputComponent = React.forwardRef((props, inputRef) => <div ref={inputRef} {...props} />)

inputComponent.displayName = 'AutocompleteInput'

inputComponent.propTypes = {
  inputRef: oneOfType([func, object]),
}

function Control(props) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps, error },
  } = props

  return (
    <components.Control {...props} css={{ border: 'none', boxShadow: 'none' }}>
      <TextField
        fullWidth
        variant="outlined"
        style={{ backgroundColor: error ? 'rgba(255,0,0,.1)' : 'white' }}
        InputProps={{
          inputComponent,
          inputProps: {
            className: TextFieldProps.margin === 'dense' ? classes.inputDense : classes.input,
            ref: innerRef,
            children,
            ...innerProps,
          },
        }}
        {...TextFieldProps}
      />
    </components.Control>
  )
}

Control.propTypes = {
  children: node.isRequired,
  innerProps: object,
  innerRef: oneOfType([func, object]),
  selectProps: object.isRequired,
}

function Option({ innerRef, isFocused, isSelected, innerProps, children, data }) {
  return (
    <MenuItem
      ref={innerRef}
      selected={isFocused}
      component="div"
      style={{
        fontWeight: isSelected ? 500 : 400,
        maxWidth: '100%',
        whiteSpace: 'normal',
        wordWrap: 'wrap',
        borderColor: 'rgba(0,0,0,0.87)',
      }}
      data-test-option-value={data?.value}
      {...innerProps}
    >
      {children}
    </MenuItem>
  )
}

Option.propTypes = {
  children: node.isRequired,
  innerProps: object,
  innerRef: oneOfType([func, object]),
  isFocused: bool,
  isSelected: bool,
  data: object,
}

function Placeholder({ selectProps, innerProps, children }) {
  return (
    <Typography
      color="text.secondary"
      className={selectProps.classes.placeholder}
      {...innerProps}
      style={{ backgroundColor: 'red' }}
    >
      {children}
    </Typography>
  )
}

Placeholder.propTypes = {
  children: node.isRequired,
  innerProps: object,
  selectProps: object.isRequired,
}

function SingleValue({ selectProps, innerProps = {}, children = null }) {
  return (
    <Typography className={selectProps.classes.singleValue} {...innerProps}>
      {children}
    </Typography>
  )
}

SingleValue.propTypes = {
  children: node,
  innerProps: object,
  selectProps: object.isRequired,
}

function ValueContainer({ selectProps, children }) {
  return <div className={selectProps.classes.valueContainer}>{children}</div>
}

ValueContainer.propTypes = {
  children: node.isRequired,
  selectProps: object.isRequired,
}

function Menu({ selectProps, innerProps, children }) {
  return (
    <Paper
      square
      className={selectProps.classes.paper}
      {...innerProps}
      style={{ borderColor: 'rgba(0,0,0,0.87)' }}
    >
      {children}
    </Paper>
  )
}

Menu.propTypes = {
  children: node.isRequired,
  innerProps: object,
  selectProps: object,
}

const LoadingIndicator = () => {
  return (
    <div
      style={{
        marginRight: 8,
        marginTop: 3,
      }}
    >
      <CircularProgress size={20} />
    </div>
  )
}

export const autocompleteComponents = {
  Control,
  Menu,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  LoadingIndicator,
}
